/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { isNil } from 'lodash';
import {
  createToken,
  FORMULA_SINGLE_CHARACTER_TOKEN_TYPE,
  FormulaToken,
  FormulaTokenDef,
  FormulaTokenType,
  KEYWORD_TOKEN_TYPE,
  NAMED_TOKEN_DEF,
  NUMBER_TOKEN_DEF,
} from './formula-token';
import { isSpaceChar, UnreadableStream, unreadableStream } from './formula-util';

export type CharStream = UnreadableStream<Maybe<string>>;
export type FormulaLexer = UnreadableStream<Maybe<FormulaToken>>;

export function charStream(text: string): CharStream {
  return unreadableStream(textIterator(text));
}

export function formulaLexer(chars: CharStream): FormulaLexer {
  return unreadableStream(tokenIterator(chars));
}

function* textIterator(text: string): IterableIterator<string> {
  for (const char of text) {
    yield char;
  }
}

function* tokenIterator(chars: CharStream): IterableIterator<FormulaToken> {
  while (true) {
    const eol = readEol(chars);
    if (eol) {
      return eol;
    }

    yield readToken(chars);
  }
}

function readToken(chars: CharStream): FormulaToken {
  let token = readSpaceToken(chars);
  if (token) {
    return token;
  }

  token = readSingleCharacterToken(chars);
  if (token) {
    return token;
  }

  token = readNumberToken(chars);
  if (token) {
    return token;
  }

  token = readNamedToken(chars);
  if (token) {
    return token;
  }

  return createToken(FormulaTokenType.UNEXPECTED, chars.position(), <string>chars.read());
}

const isEol = isNil;

function readEol(chars: CharStream): Maybe<FormulaToken> {
  const char = chars.read();

  if (isEol(char)) {
    return createToken(FormulaTokenType.EOL, chars.position() - 1, '');
  }

  chars.unread(char);
}

function readSpaceToken(chars: CharStream): Maybe<FormulaToken> {
  const buffer = [];
  while (true) {
    const char = chars.read();

    if (isEol(char) || !isSpaceChar(char)) {
      chars.unread(char);
      break;
    }
    buffer.push(char);
  }

  if (buffer.length > 0) {
    return createToken(FormulaTokenType.SPACE, chars.position() - buffer.length, buffer.join(''));
  }
}

function readSingleCharacterToken(chars: CharStream): Maybe<FormulaToken> {
  const char = <string>chars.read();

  const type = FORMULA_SINGLE_CHARACTER_TOKEN_TYPE[char];

  if (isNil(type)) {
    chars.unread(char);
  } else {
    return createToken(type, chars.position() - 1, char);
  }
}

function readContentByTokenDef(tokenDef: FormulaTokenDef, chars: CharStream): Maybe<string> {
  const char = <string>chars.read();
  const { successiveRE, completeRE } = tokenDef;

  if (successiveRE.test(char)) {
    let buffer = '';
    let nextChar: Maybe<string> = char;
    while (!isEol(nextChar) && successiveRE.test(buffer + nextChar)) {
      buffer = buffer + nextChar;
      nextChar = chars.read();
    }
    chars.unread(nextChar);

    if (!completeRE.test(buffer)) {
      for (let i = buffer.length - 1; i >= 0; i--) {
        chars.unread(buffer[i]);
      }
    } else {
      return buffer;
    }
  } else {
    chars.unread(char);
  }
}

function readNumberToken(chars: CharStream): Maybe<FormulaToken> {
  const content = readContentByTokenDef(NUMBER_TOKEN_DEF, chars);

  if (content) {
    return createToken(FormulaTokenType.NUMBER, chars.position() - content.length, content);
  }
}

function readNamedToken(chars: CharStream): Maybe<FormulaToken> {
  const content = readContentByTokenDef(NAMED_TOKEN_DEF, chars);

  if (content) {
    const keywordTokenType = KEYWORD_TOKEN_TYPE[content];

    if (keywordTokenType) {
      return createToken(keywordTokenType, chars.position() - content.length, content);
    } else {
      return createToken(FormulaTokenType.ID, chars.position() - content.length, content);
    }
  }
}
